// FILE: src/components/admin/surveyTopics.js
// The course survey on the staff side (WP-6, MTO 10.3): the five rated
// topics and the two written answers, in the words staff see, and the
// figures the survey pages show. Responses sent before the six-topic survey
// (version 1) have `ratings: null` and `legacy: { rating, comments }`.
import { DASH, formatNumber } from "./format";

export const RATED_TOPICS = [
  { id: "courseContent", label: "Course content" },
  { id: "contentSupport", label: "Course content support" },
  { id: "navigation", label: "Moving through the course" },
  { id: "technicalDifficulties", label: "Technical problems" },
  { id: "serviceSupport", label: "Student service support" }
];

export const TEXT_TOPICS = [
  { id: "suggestions", label: "How we could improve" },
  { id: "freeText", label: "Anything else" }
];

export const LEGACY_NOTE = "Sent before the six-topic survey";

/** A response from the one-question survey (before WP-6). */
export function isLegacySurvey(s) {
  return !s || !s.ratings || typeof s.ratings !== "object";
}

function score(value) {
  const n = Number(value);
  return value !== null && value !== undefined && value !== "" && Number.isFinite(n) && n > 0 ? n : null;
}

/** The response's overall rating: the average of its topics, or the old single rating. */
export function overallRating(s) {
  if (!s) return null;
  const avg = score(s.averageRating);
  if (avg !== null) return avg;
  return isLegacySurvey(s) ? score(s.legacy?.rating ?? s.rating) : null;
}

/** "4.2 out of 5" (or "—"). */
export function ratingText(value, decimals = 1) {
  const n = score(value);
  return n === null ? DASH : `${formatNumber(n, decimals)} out of 5`;
}

/** Average of the overall ratings, or null. */
export function averageOverall(list) {
  const xs = (list || []).map(overallRating).filter((v) => v !== null);
  return xs.length ? xs.reduce((t, v) => t + v, 0) / xs.length : null;
}

/** [{ id, label, average, count }] for the six-topic responses in `list`. */
export function topicAverages(list) {
  return RATED_TOPICS.map((t) => {
    const xs = (list || [])
      .filter((s) => !isLegacySurvey(s))
      .map((s) => score(s.ratings[t.id]))
      .filter((v) => v !== null);
    return { ...t, count: xs.length, average: xs.length ? xs.reduce((a, v) => a + v, 0) / xs.length : null };
  });
}

/** Trimmed text or "". */
export function textOf(value) {
  return typeof value === "string" ? value.trim() : "";
}

/** The student a response belongs to (populated `user`, or an expanded userId). */
export function surveyStudent(s) {
  if (s?.user && typeof s.user === "object") return s.user;
  return s?.userId && typeof s.userId === "object" ? s.userId : null;
}
